import { Box, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore.js";
dayjs.extend(isSameOrBefore);

export default function ReportGraph({
  rateData,
  labelType,
  setLabelType,
  siteData,
}) {
  const [aggregatedData, setAggregatedData] = useState({
    totalCount: 0,
    ai_missing_m: 0,
    ai_missing_s: 0,
    avi_missing_m: 0,
    avi_missing_s: 0,
    ai_over_m: 0,
    ai_over_s: 0,
    avi_over_m: 0,
    avi_over_s: 0,
    hrNGCount: 0,
  });

  const dataArray = Array.isArray(rateData) ? rateData : rateData.dailyRates;

  // console.log("dataArray", dataArray);

  useEffect(() => {
    if (rateData && rateData.length > 0) {
      const sumData = rateData.reduce(
        (acc, curr) => {
          return {
            totalCount: acc.totalCount + Number(curr.totalCount),
            ai_missing_m: acc.ai_missing_m + Number(curr.ai_missing_m),
            ai_missing_s: acc.ai_missing_s + Number(curr.ai_missing_s),
            avi_missing_m: acc.avi_missing_m + Number(curr.avi_missing_m),
            avi_missing_s: acc.avi_missing_s + Number(curr.avi_missing_s),
            ai_over_m: acc.ai_over_m + Number(curr.ai_over_m),
            ai_over_s: acc.ai_over_s + Number(curr.ai_over_s),
            avi_over_m: acc.avi_over_m + Number(curr.avi_over_m),
            avi_over_s: acc.avi_over_s + Number(curr.avi_over_s),
            hrNGCount: acc.hrNGCount + Number(curr.hrNGCount),
          };
        },
        {
          totalCount: 0,
          ai_missing_m: 0,
          ai_missing_s: 0,
          avi_missing_m: 0,
          avi_missing_s: 0,
          ai_over_m: 0,
          ai_over_s: 0,
          avi_over_m: 0,
          avi_over_s: 0,
          hrNGCount: 0,
        }
      );
      setAggregatedData(sumData);
    }
  }, [rateData]);

  const groupedData = dataArray.reduce((acc, curr) => {
    const date = curr.date;

    if (!acc[date]) {
      acc[date] = {
        date: date,
        totalCount: 0,
        ai_missing_m: 0,
        ai_missing_s: 0,
        avi_missing_m: 0,
        avi_missing_s: 0,
        ai_over_m: 0,
        ai_over_s: 0,
        avi_over_m: 0,
        avi_over_s: 0,
        hrNGCount: 0,
      };
    }

    acc[date].totalCount += Number(curr.totalCount || 0);
    acc[date].ai_missing_m += Number(curr.ai_missing_m || 0);
    acc[date].ai_missing_s += Number(curr.ai_missing_s || 0);
    acc[date].avi_missing_m += Number(curr.avi_missing_m || 0);
    acc[date].avi_missing_s += Number(curr.avi_missing_s || 0);
    acc[date].ai_over_m += Number(curr.ai_over_m || 0);
    acc[date].ai_over_s += Number(curr.ai_over_s || 0);
    acc[date].avi_over_m += Number(curr.avi_over_m || 0);
    acc[date].avi_over_s += Number(curr.avi_over_s || 0);
    acc[date].hrNGCount += Number(curr.hrNGCount || 0);
    acc[date].aiMissingRate =
      acc[date].ai_missing_m !== 0
        ? Number(
            ((acc[date].ai_missing_s / acc[date].ai_missing_m) * 100).toFixed(2)
          )
        : 0;

    acc[date].aviMissingRate =
      acc[date].avi_missing_m !== 0
        ? Number(
            ((acc[date].avi_missing_s / acc[date].avi_missing_m) * 100).toFixed(
              2
            )
          )
        : 0;

    acc[date].aiOverRate =
      acc[date].ai_over_m !== 0
        ? Number(((acc[date].ai_over_s / acc[date].ai_over_m) * 100).toFixed(2))
        : 0;

    acc[date].aviOverRate =
      acc[date].avi_over_m !== 0
        ? Number(
            ((acc[date].avi_over_s / acc[date].avi_over_m) * 100).toFixed(2)
          )
        : 0;

    return acc;
  }, {});

  // 객체를 배열로 변환
  const aggregatedDataArray = Object.values(groupedData);

  // console.log("일자별 집계 데이터:", aggregatedDataArray);

  const data = dataArray.map((item) => ({
    date:
      dataArray.length >= 3 ? dayjs(item.date).format("YY-MM-DD") : item.date,
    aviOverRate: item.aviOverInspectionRate,
    aiOverRate: item.aiOverInspectionRate,
    aiMissingRate: item.aiMissingInspectionRate,
    aviMissingRate: item.aviMissingInspectionRate,
    hrNGCount: item.hrNGCount,
    hrNGRate: item.hrNGRate,
    totalCount: item.totalCount,
  }));

  // totalCount가 가장 높은 데이터 추출
  const maxTotalCount = Math.max(...data.map((item) => item.totalCount));
  const maxTotalCountData = data.find(
    (item) => item.totalCount === maxTotalCount
  );

  // 세자리마다 콤마 찍기
  const formatNumber = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <>
      <Box
        sx={{
          mt: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            // p: 1,
            // border: "1px solid lightgray",
            justifyContent: "space-between",
            borderRadius: 1,
          }}
        >
          {siteData && siteData.graph.avi && (
            <Box
              sx={{
                width: "100%",
                height: "300px",
                border: "solid 1px var(--border-line-color)",
                bgcolor: "var(--bg2-color)",
              }}
            >
              <Box
                sx={{
                  p: 1,
                  display: "flex",
                  justifyContent: "center",
                  bgcolor: "var(--th-color)",
                }}
              >
                <Typography sx={{ fontSize: 17, fontWeight: "bold" }}>
                  AVI
                </Typography>
              </Box>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  data={aggregatedDataArray}
                  margin={{ top: 5, right: 30, left: -15, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend
                    wrapperStyle={{ fontSize: "13px", marginLeft: "30px" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="aviOverRate"
                    name="AVI 과검률"
                    stroke="var(--memory-color)"
                  />
                  <Line
                    type="monotone"
                    dataKey="aviMissingRate"
                    name="AVI 미검률"
                    stroke="var(--cpu-color)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
          {siteData && siteData.graph.ai && (
            <Box
              sx={{
                width: "100%",
                height: "300px",
                border: "solid 1px var(--border-line-color)",
                bgcolor: "var(--bg2-color)",
              }}
            >
              <Box
                sx={{
                  p: 1,
                  display: "flex",
                  justifyContent: "center",
                  bgcolor: "var(--th-color)",
                }}
              >
                <Typography sx={{ fontSize: 17, fontWeight: "bold" }}>
                  AI
                </Typography>
              </Box>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  data={aggregatedDataArray}
                  margin={{ top: 5, right: 30, left: -15, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "var(--paper-bg-color)", // 어두운 배경
                      border: "var(--line-text-color)",
                      borderRadius: "5px",
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: "13px", marginLeft: "30px" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="aiOverRate"
                    name="AI 과검률"
                    stroke="var(--memory-color)"
                  />
                  <Line
                    type="monotone"
                    dataKey="aiMissingRate"
                    name="AI 미검률"
                    stroke="var(--cpu-color)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
          {siteData && siteData.graph.hr && (
            <Box
              sx={{
                width: "100%",
                height: "300px",
                border: "solid 1px var(--border-line-color)",
                bgcolor: "var(--bg2-color)",
              }}
            >
              <Box
                sx={{
                  p: 1,
                  display: "flex",
                  justifyContent: "center",
                  bgcolor: "var(--th-color)",
                }}
              >
                <Typography sx={{ fontSize: 17, fontWeight: "bold" }}>
                  HR
                </Typography>
              </Box>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  data={aggregatedDataArray}
                  margin={{ top: 5, right: 30, left: -15, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis
                    domain={[0, maxTotalCount]}
                    label={{
                      value: "검사량(개)",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === "NG 개수") {
                        return [`${formatNumber(value)}개`, name];
                      }
                      if (name === "전체 이미지 개수") {
                        return [`${formatNumber(value)}개`, name];
                      }
                      return [value, name];
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: "13px", marginLeft: "30px" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="totalCount"
                    name="전체 이미지 개수"
                    stroke="var(--memory-color)"
                  />
                  <Line
                    type="monotone"
                    dataKey="hrNGCount"
                    name="NG 개수"
                    stroke="var(--cpu-color)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
